import { usePersistedState } from "@/lib/persistedFilters";
// Emergency Numbers → Domains. Every assessed domain, worst first.
import { useCallback, useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Loader2, Search } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Empty, ModePill, PLATFORM_LABEL, Pager, Pill, STATUS, STATUS_ORDER, StatusPill, ago } from "./ui";

const LIMIT = 50;

function Approval({ a, fingerprint }) {
  if (!a) return <span className="text-slate-300">—</span>;
  const stale = a.status === "approved" && a.plan_fingerprint !== fingerprint;
  const tone = a.status === "executed" ? "bg-emerald-100 text-emerald-800"
    : a.status === "refused" ? "bg-rose-100 text-rose-800"
      : stale ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800";
  return <Pill tone={tone} title={`${a.by} · ${a.approved_at || ""}`}>{stale ? "plan changed" : a.status}</Pill>;
}

// Real users on a DLR domain, by how their emergency address resolves.
function Subscribers({ d }) {
  const s = d.subscribers;
  if (d.expected_mode !== "DLR" || !s || !s.address) return <span className="text-slate-300">—</span>;
  if (!s.regular) return <span className="text-[11px] text-slate-400">no users</span>;
  const a = s.address;
  const mism = (s.mismatch || []).length;
  const intended = (s.intended || []).length;
  const parts = [
    a.own && <span key="own" className="text-emerald-700">{a.own} own</span>,
    a.inherits && <span key="inh" className="text-slate-500">{a.inherits} default</span>,
    a.none && <span key="none" className="font-semibold text-rose-700">{a.none} none</span>,
    a.unknown && <span key="unk" className="font-semibold text-amber-700">{a.unknown} unknown</span>,
    mism && <span key="mis" className="font-semibold text-amber-700">{mism} ≠ endpoint</span>,
    intended && <span key="int" className="text-slate-400">{intended} intended</span>,
  ].filter(Boolean);
  return (
    <span className="whitespace-nowrap text-[11px]" data-testid={`e911-domain-subs-${d.domain}`}
          title={`${s.regular} real user(s): own address · inherit the domain default${d.domain_default?.address_id ? ` (${d.domain_default.address_id})` : " (not set)"} · no address · address-id not found · address differs from the Endpoint's`}>
      {parts.flatMap((p, i) => (i ? [<span key={`sep${i}`} className="text-slate-300"> · </span>, p] : [p]))}
    </span>
  );
}

export default function Domains({ overview, filter, refreshKey, onOpen }) {
  const [status, setStatus] = usePersistedState("e911-domains.status", "", filter?.status);
  const [q, setQ] = usePersistedState("e911-domains.q", "");
  const [trunk, setTrunk] = usePersistedState("e911-domains.trunk", "");
  const [mismatch, setMismatch] = usePersistedState("e911-domains.mismatch", "");
  const [platform, setPlatform] = usePersistedState("e911-domains.platform", "");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const platforms = overview?.settings?.platforms || ["core"];
  const counts = overview?.domains || {};

  const lastFilter = useRef(filter);
  // A filter handed down from the Overview wins — but not on mount, where the remembered status is kept.
  useEffect(() => {
    if (lastFilter.current === filter) return;
    lastFilter.current = filter;
    setStatus(filter?.status || ""); setPage(1);
  }, [filter, setStatus]);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data: d } = await api.get("/e911/domains", { params: { status, q, trunk, mismatch, platform, page, limit: LIMIT } });
      setData(d);
    } catch {
      toast.error("Could not load the domains");
    } finally {
      setBusy(false);
    }
  }, [status, q, trunk, mismatch, platform, page]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const pages = data ? Math.max(1, Math.ceil(data.total / LIMIT)) : 1;
  const pick = (setter) => (v) => { setter(v); setPage(1); };

  return (
    <div className="space-y-4" data-testid="e911-domains">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant={status === "" ? "default" : "outline"} onClick={() => pick(setStatus)("")}
                data-testid="e911-domains-status-all">All</Button>
        {STATUS_ORDER.map((k) => (
          <Button key={k} size="sm" variant={status === k ? "default" : "outline"} onClick={() => pick(setStatus)(k)}
                  data-testid={`e911-domains-status-${k}`} title={STATUS[k].hint}>
            {STATUS[k].label}{counts[k] ? ` (${counts[k]})` : ""}
          </Button>
        ))}
        <span className="mx-1 h-6 w-px bg-slate-200" />
        {[["", "Any"], ["yes", "SIP trunk"], ["no", "No trunk"]].map(([v, label]) => (
          <Button key={v || "any"} size="sm" variant={trunk === v ? "default" : "outline"} onClick={() => pick(setTrunk)(v)}
                  data-testid={`e911-domains-trunk-${v || "any"}`}>{label}</Button>
        ))}
        <span className="mx-1 h-6 w-px bg-slate-200" />
        <Button size="sm" variant={mismatch === "yes" ? "default" : "outline"} onClick={() => pick(setMismatch)(mismatch === "yes" ? "" : "yes")}
                data-testid="e911-domains-mismatch"
                title="Only domains where a real user's own address differs from the one their emergency number's Endpoint carries (verdict unchanged — a 911 call sends the user's own address)">
          ≠ Endpoint
        </Button>
        {platforms.length > 1 && (
          <select value={platform} onChange={(e) => pick(setPlatform)(e.target.value)} data-testid="e911-domains-platform"
                  className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
            <option value="">All platforms</option>
            {platforms.map((p) => <option key={p} value={p}>{PLATFORM_LABEL[p] || p}</option>)}
          </select>
        )}
        <div className="relative ml-auto">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="w-64 pl-8" placeholder="Domain or territory" value={q} data-testid="e911-domains-search"
                 onChange={(e) => pick(setQ)(e.target.value)} />
        </div>
        {busy && <Loader2 className="h-4 w-4 animate-spin text-slate-300" />}
        <span className="text-xs text-slate-500" data-testid="e911-domains-total">{(data?.total || 0).toLocaleString()} domains</span>
      </div>

      {status && <p className="text-[12px] text-slate-500" data-testid="e911-domains-hint">{STATUS[status]?.hint}</p>}

      <Card className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-1.5">Domain</th>
              {platforms.length > 1 && <th className="px-3 py-1.5">Platform</th>}
              <th className="px-3 py-1.5">Trunk</th>
              <th className="px-3 py-1.5">Users</th>
              <th className="px-3 py-1.5">Mode</th>
              <th className="px-3 py-1.5">Status</th>
              <th className="px-3 py-1.5" title="Emergency Addresses · Endpoints · Legacy numbers · count API">Records</th>
              <th className="px-3 py-1.5" title="Real users on a DLR domain: own address · domain default · no address · unknown id">Subscribers</th>
              <th className="px-3 py-1.5">Plan</th>
              <th className="px-3 py-1.5">Approval</th>
              <th className="px-3 py-1.5">Assessed</th>
            </tr>
          </thead>
          <tbody data-testid="e911-domains-body">
            {(data?.items || []).map((d) => {
              const c = d.counts || {};
              return (
                <tr key={`${d.platform}/${d.domain}`} onClick={() => onOpen({ platform: d.platform, domain: d.domain })}
                    className="cursor-pointer border-t border-slate-100 hover:bg-slate-50" data-testid={`e911-domain-row-${d.domain}`}>
                  <td className="px-3 py-1.5">
                    <div className="font-medium text-slate-800">{d.domain}</div>
                    {d.territory && <div className="text-[11px] text-slate-400">{d.territory}</div>}
                  </td>
                  {platforms.length > 1 && <td className="px-3 py-1.5 text-slate-600">{PLATFORM_LABEL[d.platform] || d.platform}</td>}
                  <td className="px-3 py-1.5">{d.has_siptrunk ? <Pill tone="bg-indigo-50 text-indigo-700">trunk</Pill> : <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 py-1.5 text-slate-600">{d.regular_users ?? "—"}</td>
                  <td className="px-3 py-1.5"><ModePill mode={d.expected_mode} /></td>
                  <td className="px-3 py-1.5"><StatusPill status={d.status} testid={`e911-domain-status-${d.domain}`} /></td>
                  <td className="px-3 py-1.5 font-mono text-[11px] text-slate-600" title="addresses · endpoints · legacy · count API">
                    {d.error ? <span className="text-rose-600">error</span>
                      : `${c.addresses ?? 0} · ${c.endpoints ?? 0} · ${c.legacy ?? 0} · ${c.api_total ?? "?"}`}
                  </td>
                  <td className="px-3 py-1.5"><Subscribers d={d} /></td>
                  <td className="px-3 py-1.5 text-[12px]">
                    {d.blocker_count ? <span className="font-medium text-rose-700">{d.blocker_count} blocker{d.blocker_count === 1 ? "" : "s"}</span>
                      : d.actions ? <span className="font-medium text-amber-700">{d.actions} action{d.actions === 1 ? "" : "s"}</span>
                        : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-1.5"><Approval a={d.approval} fingerprint={d.plan_fingerprint} /></td>
                  <td className="px-3 py-1.5 text-[11px] text-slate-500" title={d.assessed_at}>{ago(d.assessed_at)}</td>
                </tr>
              );
            })}
            {data && !data.items.length && (
              <Empty colSpan={11} testid="e911-domains-empty">
                {overview && !Object.keys(counts).length ? "Nothing assessed yet — run a scan from the Overview tab." : "No domain matches those filters."}
              </Empty>
            )}
          </tbody>
        </table>
      </Card>
      <Pager page={page} pages={pages} onPage={setPage} testid="e911-domains" />
    </div>
  );
}
